import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Code, 
  Copy, 
  CheckCircle2,
  ExternalLink,
  Palette,
  Globe,
  ArrowLeft,
  Home
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const BADGE_STYLES = [
  { id: "default", name: "Default", preview: "White background" },
  { id: "minimal", name: "Minimal", preview: "Compact single-line" },
  { id: "dark", name: "Dark", preview: "Dark theme" },
  { id: "gradient", name: "Gradient", preview: "Purple gradient" }
];

export default function BadgesWidgetsPage() {
  const navigate = useNavigate();
  const [domain, setDomain] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("default");
  const [embedCode, setEmbedCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (domain) {
      setPreviewUrl(`${BACKEND_URL}/api/badges/seo/${encodeURIComponent(domain)}?style=${selectedStyle}`);
    }
  }, [domain, selectedStyle]);

  const generateEmbedCode = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain");
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/badges/embed-code/${encodeURIComponent(domain)}?style=${selectedStyle}`);
      if (response.data.success) {
        setEmbedCode(response.data);
        toast.success("Embed code generated!");
      }
    } catch (error) {
      toast.error("Failed to generate embed code");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-white hover:bg-white/20">
              <Home className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-white/20 rounded-xl">
              <Code className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Website Badges & Widgets</h1>
              <p className="text-emerald-200">Embed SEO score badges on your website</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Configuration */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Configure Badge</CardTitle>
                <CardDescription>Enter your domain and choose a style</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Your Domain</label>
                  <Input
                    placeholder="example.com"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Badge Style</label>
                  <div className="grid grid-cols-2 gap-3">
                    {BADGE_STYLES.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => setSelectedStyle(style.id)}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          selectedStyle === style.id 
                            ? "border-emerald-500 bg-emerald-50" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <span className="font-medium block">{style.name}</span>
                        <span className="text-xs text-gray-500">{style.preview}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={generateEmbedCode}
                  disabled={!domain.trim()}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Code className="w-4 h-4 mr-2" />
                  Generate Embed Code
                </Button>
              </CardContent>
            </Card>

            {/* Social Proof Widget */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Social Proof Widget</CardTitle>
                <CardDescription>Show trust metrics on your site</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-gray-50 rounded-lg mb-4">
                  <img 
                    src={`${BACKEND_URL}/api/badges/social-proof`} 
                    alt="Social Proof Widget"
                    className="max-w-full"
                  />
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => copyToClipboard(`<img src="${BACKEND_URL}/api/badges/social-proof" alt="Trusted by 50,000+ marketers" />`)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Widget Code
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Preview & Code */}
          <div className="space-y-6">
            {/* Badge Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Badge Preview</CardTitle>
              </CardHeader>
              <CardContent>
                {domain ? (
                  <div className="p-8 bg-gray-100 rounded-lg flex items-center justify-center min-h-[150px]">
                    <img 
                      src={previewUrl} 
                      alt="SEO Badge Preview"
                      className="max-w-full"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                ) : (
                  <div className="p-8 bg-gray-50 rounded-lg text-center min-h-[150px] flex items-center justify-center">
                    <div>
                      <Globe className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">Enter a domain to see preview</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Embed Codes */}
            {embedCode && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Embed Code</CardTitle>
                    <Badge className="bg-green-100 text-green-700">Ready</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* HTML Code */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">HTML</span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => copyToClipboard(embedCode.embed_html)}
                      >
                        {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto">
                      {embedCode.embed_html}
                    </pre>
                  </div>

                  {/* Markdown Code */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Markdown</span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => copyToClipboard(embedCode.embed_markdown)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto">
                      {embedCode.embed_markdown}
                    </pre>
                  </div>

                  {/* Direct URL */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium block mb-1">Direct Badge URL</span>
                    <div className="flex gap-2">
                      <Input value={embedCode.badge_url} readOnly className="text-xs" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(embedCode.badge_url)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
